import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarClock, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";

export interface AdminOfferWindow {
  id: number;
  label: string;
  opensAt: string;
  closesAt: string;
  active: boolean;
  webinarTitle: string | null;
  webinarReplayUrl: string | null;
  presentationUrl?: string | null;
  qaUrl?: string | null;
  qaClosesAt?: string | null;
  promotionLabel?: string | null;
  bonusClosesAt?: string | null;
  refundFloorAt?: string | null;
  eventTerms?: string | null;
  standardLocks?: boolean | null;
  updatedBy: string | null;
  updatedAt: string;
}

interface OfferWindowsResponse {
  windows: AdminOfferWindow[];
  history: Array<{ id: number; changedBy: string; field: string; changedAt: string }>;
}

const QUERY_KEY = ["/api/admin/offer-windows"] as const;

type FormState = {
  label: string;
  opensAt: string;
  closesAt: string;
  webinarTitle: string;
  webinarReplayUrl: string;
  presentationUrl: string;
  qaUrl: string;
  qaClosesAt: string;
  promotionLabel: string;
  bonusClosesAt: string;
  refundFloorAt: string;
  eventTerms: string;
  standardLocks: boolean;
};

const EMPTY_FORM: FormState = {
  label: "", opensAt: "", closesAt: "", webinarTitle: "", webinarReplayUrl: "",
  presentationUrl: "", qaUrl: "", qaClosesAt: "", promotionLabel: "", bonusClosesAt: "",
  refundFloorAt: "", eventTerms: "", standardLocks: false,
};

/** ISO → value for <input type="datetime-local"> in the admin's own timezone. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString();
}

function formatEt(iso: string): string {
  return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) + " ET";
}

export function windowStatus(w: AdminOfferWindow, now: Date = new Date()): "open" | "scheduled" | "ended" | "closed" {
  if (!w.active) return "closed";
  const t = now.getTime();
  if (new Date(w.opensAt).getTime() > t) return "scheduled";
  if (new Date(w.closesAt).getTime() <= t) return "ended";
  return "open";
}

/** Admin → Billing: add, edit or close the reusable SYRE offer windows (B23). */
export function OfferWindowsCard() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const { data, isLoading, error } = useQuery<OfferWindowsResponse>({ queryKey: QUERY_KEY });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        label: form.label.trim(),
        opensAt: fromLocalInput(form.opensAt),
        closesAt: fromLocalInput(form.closesAt),
        webinarTitle: form.webinarTitle.trim() || null,
        webinarReplayUrl: form.webinarReplayUrl.trim() || null,
        presentationUrl: form.presentationUrl.trim() || null,
        qaUrl: form.qaUrl.trim() || null,
        qaClosesAt: form.qaClosesAt ? fromLocalInput(form.qaClosesAt) : null,
        promotionLabel: form.promotionLabel.trim() || null,
        bonusClosesAt: form.bonusClosesAt ? fromLocalInput(form.bonusClosesAt) : null,
        refundFloorAt: form.refundFloorAt ? fromLocalInput(form.refundFloorAt) : null,
        eventTerms: form.eventTerms.trim() || null,
        standardLocks: form.standardLocks,
      };
      const res = editingId === "new"
        ? await apiRequest("POST", "/api/admin/offer-windows", body)
        : await apiRequest("PATCH", `/api/admin/offer-windows/${editingId}`, body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: editingId === "new" ? "Offer window added" : "Offer window saved" });
      setEditingId(null);
      setForm(EMPTY_FORM);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["/api/offers/syre-lifetime"] });
    },
    onError: (err) => toast({ title: "Couldn't save the window", description: parseErrorMessage(err, "Check the dates and try again."), variant: "destructive" }),
  });

  const closeMutation = useMutation({
    mutationFn: async (id: number) => (await apiRequest("POST", `/api/admin/offer-windows/${id}/close`, {})).json(),
    onSuccess: () => {
      toast({ title: "Offer window closed" });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["/api/offers/syre-lifetime"] });
    },
    onError: (err) => toast({ title: "Couldn't close the window", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  function startEdit(w: AdminOfferWindow) {
    setEditingId(w.id);
    setForm({
      label: w.label,
      opensAt: toLocalInput(w.opensAt),
      closesAt: toLocalInput(w.closesAt),
      webinarTitle: w.webinarTitle ?? "",
      webinarReplayUrl: w.webinarReplayUrl ?? "",
      presentationUrl: w.presentationUrl ?? "",
      qaUrl: w.qaUrl ?? "",
      qaClosesAt: w.qaClosesAt ? toLocalInput(w.qaClosesAt) : "",
      promotionLabel: w.promotionLabel ?? "",
      bonusClosesAt: w.bonusClosesAt ? toLocalInput(w.bonusClosesAt) : "",
      refundFloorAt: w.refundFloorAt ? toLocalInput(w.refundFloorAt) : "",
      eventTerms: w.eventTerms ?? "",
      standardLocks: w.standardLocks ?? false,
    });
  }

  const canSave = form.label.trim().length >= 2 && form.opensAt !== "" && form.closesAt !== "";

  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-offer-windows">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-brand" />
          <h4 className="font-semibold">SYRE offer windows</h4>
        </div>
        {editingId === null && (
          <Button size="sm" variant="outline" onClick={() => { setEditingId("new"); setForm(EMPTY_FORM); }} data-testid="button-add-offer-window">
            Add window
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        /syre is open only inside an active window. Adding a window reopens the offer with no deploy. The most recent replay
        URL shows on /syre in both the open and closed states.
      </p>

      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">Couldn't load offer windows.</p>
      ) : (data?.windows.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="text-no-offer-windows">No windows yet. The offer is closed.</p>
      ) : (
        <ul className="divide-y rounded-md border" data-testid="list-offer-windows">
          {data!.windows.map((w) => {
            const status = windowStatus(w);
            return (
              <li key={w.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`row-offer-window-${w.id}`}>
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{w.label}</span>
                    <Badge variant={status === "open" ? "default" : "secondary"} data-testid={`status-offer-window-${w.id}`}>{status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{formatEt(w.opensAt)} → {formatEt(w.closesAt)}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    Replay: {w.webinarReplayUrl ? w.webinarReplayUrl : "none yet"}
                    {w.updatedBy ? ` · last change by ${w.updatedBy}` : ""}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => startEdit(w)} disabled={editingId !== null} data-testid={`button-edit-offer-window-${w.id}`}>Edit</Button>
                  {status === "open" || status === "scheduled" ? (
                    <Button size="sm" variant="outline" onClick={() => closeMutation.mutate(w.id)} disabled={closeMutation.isPending} data-testid={`button-close-offer-window-${w.id}`}>
                      Close now
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editingId !== null && (
        <div className="space-y-3 rounded-md border p-3" data-testid="form-offer-window">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="offer-window-label">Label</Label>
              <Input id="offer-window-label" placeholder="SYRE Mastermind, Oct 2026" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="input-offer-window-label" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-opens">Opens (your local time)</Label>
              <Input id="offer-window-opens" type="datetime-local" value={form.opensAt} onChange={(e) => setForm({ ...form, opensAt: e.target.value })} data-testid="input-offer-window-opens" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-closes">Closes (your local time)</Label>
              <Input id="offer-window-closes" type="datetime-local" value={form.closesAt} onChange={(e) => setForm({ ...form, closesAt: e.target.value })} data-testid="input-offer-window-closes" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-webinar-title">Webinar title (optional)</Label>
              <Input id="offer-window-webinar-title" value={form.webinarTitle} onChange={(e) => setForm({ ...form, webinarTitle: e.target.value })} data-testid="input-offer-window-webinar-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-replay">Replay URL (optional)</Label>
              <Input id="offer-window-replay" placeholder="https://youtu.be/…" value={form.webinarReplayUrl} onChange={(e) => setForm({ ...form, webinarReplayUrl: e.target.value })} data-testid="input-offer-window-replay" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-presentation">Presentation URL (optional)</Label>
              <Input id="offer-window-presentation" value={form.presentationUrl} onChange={(e) => setForm({ ...form, presentationUrl: e.target.value })} data-testid="input-offer-window-presentation" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-qa">Q&amp;A URL (optional)</Label>
              <Input id="offer-window-qa" value={form.qaUrl} onChange={(e) => setForm({ ...form, qaUrl: e.target.value })} data-testid="input-offer-window-qa" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-qa-closes">Q&amp;A closes (your local time, optional)</Label>
              <Input id="offer-window-qa-closes" type="datetime-local" value={form.qaClosesAt} onChange={(e) => setForm({ ...form, qaClosesAt: e.target.value })} data-testid="input-offer-window-qa-closes" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-promotion-label">Promotion label (optional)</Label>
              <Input id="offer-window-promotion-label" value={form.promotionLabel} onChange={(e) => setForm({ ...form, promotionLabel: e.target.value })} data-testid="input-offer-window-promotion-label" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-bonus-closes">Bonus payment cutoff (your local time, optional)</Label>
              <Input id="offer-window-bonus-closes" type="datetime-local" value={form.bonusClosesAt} onChange={(e) => setForm({ ...form, bonusClosesAt: e.target.value })} data-testid="input-offer-window-bonus-closes" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-window-refund-floor">Refund floor (your local time, optional)</Label>
              <Input id="offer-window-refund-floor" type="datetime-local" value={form.refundFloorAt} onChange={(e) => setForm({ ...form, refundFloorAt: e.target.value })} data-testid="input-offer-window-refund-floor" />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input type="checkbox" checked={form.standardLocks} onChange={(e) => setForm({ ...form, standardLocks: e.target.checked })} data-testid="input-offer-window-standard-locks" />
              Include standard locks
            </label>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="offer-window-event-terms">Event terms (optional)</Label>
              <textarea id="offer-window-event-terms" rows={4} maxLength={500} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.eventTerms} onChange={(e) => setForm({ ...form, eventTerms: e.target.value })} data-testid="input-offer-window-event-terms" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} data-testid="button-cancel-offer-window">Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending} data-testid="button-save-offer-window">
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingId === "new" ? "Add window" : "Save window"}
            </Button>
          </div>
        </div>
      )}

      {(data?.history.length ?? 0) > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">Change history</summary>
          <ul className="mt-2 space-y-1" data-testid="list-offer-window-history">
            {data!.history.map((h) => (
              <li key={h.id}>{new Date(h.changedAt).toLocaleString()} · {h.field.replace(/_/g, " ")} by {h.changedBy}</li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
